if (!customElements.get('collection-product-search')) {
  class CollectionProductSearch extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;
      window.requestAnimationFrame(() => this.initialise());
    }

    initialise() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.clearButton = this.querySelector('.hdt-collection-search__clear');
      this.panel = this.querySelector('.hdt-collection-search__panel');
      this.status = this.querySelector('.hdt-collection-search__status');
      this.results = this.querySelector('.hdt-collection-search__results');
      this.handle = this.dataset.collectionHandle;
      this.collectionTitle = this.dataset.collectionTitle;
      this.products = null;
      this.activeIndex = -1;
      this.timer = null;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.clearButton.addEventListener('click', () => this.clear());
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
    }

    async loadProducts() {
      if (this.products) return this.products;
      const cacheKey = `collection-search:${this.handle}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          this.products = JSON.parse(cached);
          return this.products;
        }
      } catch (error) {}

      const products = [];
      const root = window.Shopify?.routes?.root || '/';
      for (let page = 1; page <= 8; page += 1) {
        const url = `${root}collections/${encodeURIComponent(this.handle)}/products.json?limit=250&page=${page}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Collection products could not be loaded');
        const data = await response.json();
        const batch = Array.isArray(data.products) ? data.products : [];
        products.push(...batch);
        if (batch.length < 250) break;
      }

      this.products = products;
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(products));
      } catch (error) {}
      return products;
    }

    onInput() {
      const query = this.input.value.trim();
      this.clearButton.hidden = !query;
      window.clearTimeout(this.timer);
      if (!query) {
        this.close();
        return;
      }
      this.timer = window.setTimeout(() => this.search(query), 180);
    }

    async search(query) {
      this.open();
      this.status.textContent = 'Searching…';
      this.results.replaceChildren();
      try {
        const products = await this.loadProducts();
        if (this.input.value.trim() !== query) return;
        const terms = this.normalise(query).split(/\s+/).filter(Boolean);
        const matches = products.filter((product) => {
          const searchable = this.normalise([
            product.title,
            product.vendor,
            product.product_type,
            ...(Array.isArray(product.tags) ? product.tags : [])
          ].filter(Boolean).join(' '));
          return terms.every((term) => searchable.includes(term));
        }).slice(0, 10);
        this.render(matches, query);
      } catch (error) {
        this.status.textContent = 'Sorry, the collection search is unavailable just now.';
      }
    }

    render(matches, query) {
      this.results.replaceChildren();
      this.activeIndex = -1;
      if (!matches.length) {
        this.status.textContent = `No products found in ${this.collectionTitle} for “${query}”.`;
        return;
      }

      this.status.textContent = `${matches.length} ${matches.length === 1 ? 'product' : 'products'} found in ${this.collectionTitle}.`;
      const root = window.Shopify?.routes?.root || '/';
      matches.forEach((product) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');
        const link = document.createElement('a');
        link.className = 'hdt-collection-search__link';
        link.href = `${root}products/${encodeURIComponent(product.handle)}`;
        link.setAttribute('aria-selected', 'false');

        const image = document.createElement('img');
        image.className = 'hdt-collection-search__image';
        image.alt = '';
        image.loading = 'lazy';
        image.width = 112;
        image.height = 112;
        if (product.images?.[0]?.src) image.src = product.images[0].src;

        const copy = document.createElement('span');
        const title = document.createElement('span');
        title.className = 'hdt-collection-search__title';
        title.textContent = product.title;
        copy.append(title);
        if (product.vendor || product.product_type) {
          const meta = document.createElement('span');
          meta.className = 'hdt-collection-search__meta';
          meta.textContent = [product.vendor, product.product_type].filter(Boolean).join(' · ');
          copy.append(meta);
        }
        link.append(image, copy);
        item.append(link);
        this.results.append(item);
      });
    }

    onKeydown(event) {
      const links = [...this.results.querySelectorAll('a')];
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (!links.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      this.activeIndex = event.key === 'ArrowDown'
        ? (this.activeIndex + 1) % links.length
        : (this.activeIndex - 1 + links.length) % links.length;
      links.forEach((link, index) => link.setAttribute('aria-selected', String(index === this.activeIndex)));
      links[this.activeIndex].scrollIntoView({ block: 'nearest' });
    }

    onSubmit(event) {
      event.preventDefault();
      const links = [...this.results.querySelectorAll('a')];
      const selected = links[this.activeIndex] || links[0];
      if (selected) window.location.assign(selected.href);
    }

    clear() {
      this.input.value = '';
      this.clearButton.hidden = true;
      this.close();
      this.input.focus();
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
    }

    normalise(value) {
      return String(value || '').toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
  }

  customElements.define('collection-product-search', CollectionProductSearch);
}
